<svelte:options
  customElement={{
    tag: "oc-switch-v3",
    /*                       */
    extend: window.__components.extend(),
    props: {
      labelPlacement: { type: "String", attribute: "label-placement" },
      loading: { type: "Boolean" },
      size: { type: "String" },
      widthBehavior: { type: "String", attribute: "width-behavior" },
    },
  }}
/>

<script lang="ts">
  import { fade } from "svelte/transition";
  import { onMount } from "svelte";
  import { runWithRenderSlice } from "@otto-ec/global-resources/core";
  import type { Props } from "./SwitchV3.types.js";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";
  import ActionSlot from "../../../common/components/action/ActionSlot.svelte";
  import type { ActionState } from "../../../common/components/action/ActionSlotProps";

  let {
    size = "100",
    labelPlacement = "right",
    loading = false,
    widthBehavior = "fit",
  }: Props = $props();

  let Host = $host();

  let actionState = $state<ActionState>({});

  let mounted = $state(false);
  onMount(() =>
    runWithRenderSlice(() => {
      mounted = true;
    }),
  );
</script>

<ActionSlot {Host} bind:actionState />
<div
  class={`root --size-${size}`}
  class:--disabled={actionState.disabled}
  class:--checked={actionState.checked}
  class:--active={actionState.active}
  class:--hover={actionState.hover}
  class:--loading={loading}
  class:--label-left={labelPlacement === "left"}
  class:--mounted={mounted}
>
  <div class="switch">
    <div class="handle">
      {#if loading}
        <oc-spinner-v1
          in:fade={{ /*                            */ duration: 100 }}
          variant="default"
          size={/*                      */ "50"}
        ></oc-spinner-v1>
      {/if}
      <oc-icon-v1 type="check" size={/*                      */ "50"}></oc-icon-v1>
    </div>
  </div>
  <slot name="label" class="label"></slot>
</div>

<DefaultStyles {widthBehavior} />

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";

  :global {
    :host {
      display: block;
      position: relative;

      pointer-events: none;
    }

    .root {
      display: flex;
      column-gap: tokens.$oc-component-switch-100-gap-x;
      align-items: center;
      justify-content: space-between;
      font: tokens.$oc-component-switch-100-label-font;

      --switch-height: #{tokens.$oc-component-switch-100-height};
      --switch-width: #{tokens.$oc-component-switch-100-width};
      --switch-background: #{tokens.$oc-component-switch-default-background-color};

      --label-color: #{tokens.$oc-component-switch-default-label-color};

      --icon-size: #{tokens.$oc-component-switch-100-icon-size};
      --icon-display: none;

      --handle-offset: calc((var(--switch-height) - var(--handle-size)) / 2);
      --handle-left: var(--handle-offset);
      --handle-size: #{tokens.$oc-component-switch-100-handle-size};
      --handle-color: #{tokens.$oc-component-switch-default-handle-color};
    }

    .label {
      color: var(--label-color);
    }

    .switch {
      display: block;
      position: relative;
      height: var(--switch-height);
      width: var(--switch-width);
      flex: 0 0 var(--switch-width);
      background: var(--switch-background);
      border-radius: 1rem;
    }

    .handle {
      display: flex;
      align-items: center;
      justify-content: center;

      position: absolute;
      top: var(--handle-offset);
      left: var(--handle-left);

      background: var(--handle-color);
      width: var(--handle-size);
      height: var(--handle-size);
      border-radius: 50%;

      > oc-icon-v1 {
        display: var(--icon-display);
        width: var(--icon-size);
        height: var(--icon-size);
        color: var(--switch-background);
      }
    }

    .--size-50 {
      font: tokens.$oc-component-switch-50-label-font;
      --switch-height: #{tokens.$oc-component-switch-50-height};
      --switch-width: #{tokens.$oc-component-switch-50-width};
      --handle-size: #{tokens.$oc-component-switch-50-handle-size};
      --icon-size: #{tokens.$oc-component-switch-50-icon-size};
    }

    .--label-left {
      flex-direction: row-reverse;
    }

    .--hover {
      --switch-background: #{tokens.$oc-component-switch-default-background-color-hover};
    }

    .--active {
      --switch-background: #{tokens.$oc-component-switch-default-background-color-active};
    }

    .--checked {
      --switch-background: #{tokens.$oc-component-switch-checked-background-color};
      --handle-left: calc(var(--switch-width) - var(--handle-size) - var(--handle-offset));

      &:not(.--loading) {
        --icon-display: block;
      }
    }

    .--hover.--checked {
      --switch-background: #{tokens.$oc-component-switch-checked-background-color-hover};
    }

    .--active.--checked {
      --switch-background: #{tokens.$oc-component-switch-checked-background-color-active};
    }

    .root.--disabled {
      --switch-background: #{tokens.$oc-component-switch-disabled-background-color};
      --handle-color: #{tokens.$oc-component-switch-disabled-handle-color};
      --label-color: #{tokens.$oc-component-switch-disabled-label-color};
    }

    /*          */
    /*                                                                      */
    @media (prefers-reduced-motion: no-preference) {
      .--mounted {
        .switch {
          transition: background-color tokens.$oc-component-switch-transition-duration
            tokens.$oc-component-switch-transition-easing;
        }

        .handle {
          transition: left tokens.$oc-component-switch-transition-duration
            tokens.$oc-component-switch-transition-easing;
        }
      }
    }
  }
</style>
